import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function CanCountCell({ sh, busy, onCount, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const countRef = useRef(null);

  useEffect(() => {
    if (!showDetail) return;
    const updatePos = () => {
      if (!countRef.current) return;
      const r = countRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [showDetail]);

  let breakdown = null, aiNotes = null, confidence = null;
  if (sh.can_count_notes) {
    try {
      const parsed = JSON.parse(sh.can_count_notes);
      breakdown = parsed.breakdown;
      aiNotes = parsed.ai_notes;
      confidence = parsed.confidence;
    } catch {}
  }

  const editBtn = (
    <button onClick={()=>{ setVal(sh.can_count ?? ''); setEditing(true); }} title="Fill/edit manually"
      style={{background:'transparent',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:11,padding:0}}>✏</button>
  );

  if (editing) {
    return (
      <div style={{display:'flex',gap:4,alignItems:'center'}}>
        <input type="number" min={0} value={val} onChange={e=>setVal(e.target.value)}
          style={{width:46,background:'var(--bg-tertiary)',border:'1px solid var(--accent)',borderRadius:5,padding:'3px 6px',fontSize:12,color:'var(--text-primary)',outline:'none',fontVariantNumeric:"tabular-nums",fontWeight:600}}
          autoFocus
          onKeyDown={e=>{
            if (e.key==='Enter') { onUpdate(sh.id, val); setEditing(false); }
            if (e.key==='Escape') { setEditing(false); }
          }} />
        <button onClick={()=>{onUpdate(sh.id, val); setEditing(false);}}
          style={{background:'var(--accent-bg)',border:'1px solid rgba(94,161,255,0.3)',color:'var(--accent)',borderRadius:4,padding:'2px 5px',fontSize:11,cursor:'pointer'}}>✓</button>
        <button onClick={()=>setEditing(false)}
          style={{background:'transparent',border:'1px solid rgba(15,23,42,0.10)',color:'var(--text-muted)',borderRadius:4,padding:'2px 5px',fontSize:11,cursor:'pointer'}}>×</button>
      </div>
    );
  }

  if (!sh.has_inside_image) {
    return (
      <div style={{display:'flex',alignItems:'center',gap:4}}>
        <span style={{opacity:0.25,fontSize:11}}>—</span>
        {editBtn}
      </div>
    );
  }

  if (busy) {
    return <span style={{fontSize:11,color:'var(--color-warning)'}}>⏳ Counting...</span>;
  }

  if (sh.can_count_status === 'error') {
    return (
      <div style={{display:'flex',alignItems:'center',gap:4}}>
        <button onClick={()=>onCount(sh.id)} title="Count failed — retry"
          style={{background:'rgba(220,38,38,0.10)',border:'1px solid rgba(220,38,38,0.25)',color:'var(--color-danger)',borderRadius:5,padding:'3px 8px',fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}>
          ⚠ Error · Retry
        </button>
        {editBtn}
      </div>
    );
  }

  if (sh.can_count == null) {
    return (
      <div style={{display:'flex',alignItems:'center',gap:4}}>
        <button onClick={()=>onCount(sh.id)} title="AI count"
          style={{background:'rgba(124,58,237,0.10)',border:'1px solid rgba(124,58,237,0.30)',color:'var(--color-purple)',borderRadius:5,padding:'3px 8px',fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}>
          🤖 Count
        </button>
        {editBtn}
      </div>
    );
  }

  const isManual = sh.can_count_status === 'manual';
  const confColor = confidence === 'high' ? 'var(--accent)' : confidence === 'medium' ? 'var(--color-warning)' : 'var(--color-danger)';

  return (
    <div style={{display:'flex',alignItems:'center',gap:4,position:'relative'}}>
      <span
        ref={countRef}
        onMouseEnter={()=>setShowDetail(true)}
        onMouseLeave={()=>setShowDetail(false)}
        style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontWeight:700,fontSize:13,color:isManual?'var(--color-info)':'var(--accent)',cursor:'help'}}>
        {sh.can_count}
      </span>
      <span style={{fontSize:9,opacity:0.5}} title={isManual?'Manual':'AI'}>{isManual?'✋':'🤖'}</span>
      {!isManual && confidence && (
        <span title={`Confidence: ${confidence}`} style={{width:6,height:6,borderRadius:'50%',background:confColor,flexShrink:0}} />
      )}
      <button onClick={()=>{setVal(sh.can_count); setEditing(true);}} title="Manual edit"
        style={{background:'transparent',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:11,padding:0}}>✏</button>
      <button onClick={()=>onCount(sh.id)} title="Re-count with AI"
        style={{background:'transparent',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:11,padding:0}}>🔄</button>

      {showDetail && (breakdown?.length > 0 || aiNotes) && createPortal(
        <div style={{position:'fixed',top:pos.top,left:pos.left,zIndex:9999,background:'var(--bg-secondary)',border:'1px solid rgba(15,23,42,0.10)',borderRadius:8,padding:'8px 10px',minWidth:180,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',pointerEvents:'none'}}>
          {breakdown?.length > 0 && (
            <>
              <div style={{fontSize:9,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,textTransform:'uppercase',marginBottom:4}}>Details</div>
              {breakdown.map((b,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',gap:10,fontSize:11,color:'var(--text-primary)',padding:'2px 0'}}>
                  <span>{b.label}</span>
                  <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontWeight:700,color:'var(--accent)'}}>{b.count}</span>
                </div>
              ))}
            </>
          )}
          {aiNotes && (
            <div style={{fontSize:10,color:'var(--text-secondary)',marginTop:6,fontStyle:'italic'}}>{aiNotes}</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
